import { useState, useEffect, useCallback } from 'react';
import { 
  getRestaurantRatings, 
  getRatingAnalytics, 
  getUserRating,
  submitRating,
  getTopRatedRestaurants
} from '../utils/restaurantApi';

/**
 * Custom hook for managing rating operations
 * Provides rating data fetching, caching, and submission functionality
 */
export const useRatings = (restaurantId) => {
  const [ratings, setRatings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [userRating, setUserRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRatings: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Fetch ratings with options
  const fetchRatings = useCallback(async (options = {}) => {
    if (!restaurantId) return;

    try {
      setLoading(true);
      setError(null);

      const [ratingsData, analyticsData, userRatingData] = await Promise.all([
        getRestaurantRatings(restaurantId, options),
        getRatingAnalytics(restaurantId).catch(() => null), // Don't fail if analytics unavailable
        getUserRating(restaurantId).catch(() => null) // Don't fail if user hasn't rated
      ]);
      
      setRatings(ratingsData.ratings || []);
      setPagination(ratingsData.pagination || pagination);
      
      if (analyticsData) {
        setAnalytics(analyticsData);
      }
      
      setUserRating(userRatingData);
      
    } catch (err) {
      console.error('Error fetching ratings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  // Submit a new rating
  const submitUserRating = useCallback(async (ratingData) => {
    try {
      const result = await submitRating({
        ...ratingData,
        restaurantId: restaurantId
      });
      
      // Refresh data after submission
      await fetchRatings();
      return result;
    } catch (err) {
      throw new Error(err.message);
    }
  }, [restaurantId, fetchRatings]);

  // Load more ratings (pagination)
  const loadMore = useCallback(async () => {
    if (!pagination.hasNextPage || loading) return;

    try {
      const nextPage = pagination.currentPage + 1;
      const moreRatings = await getRestaurantRatings(restaurantId, { 
        page: nextPage,
        limit: 10 
      });
      
      setRatings(prev => [...prev, ...(moreRatings.ratings || [])]);
      setPagination(prevPagination => moreRatings.pagination || prevPagination);
    } catch (err) {
      console.error('Error loading more ratings:', err);
    }
  }, [restaurantId, loading]);

  // Refresh all data
  const refresh = useCallback(() => {
    fetchRatings();
  }, [fetchRatings]);

  useEffect(() => {
    if (restaurantId) {
      fetchRatings();
    }
  }, [restaurantId, fetchRatings]);

  return {
    ratings,
    analytics,
    userRating,
    loading,
    error,
    pagination,
    fetchRatings,
    submitRating: submitUserRating,
    loadMore,
    refresh
  };
};

/**
 * Hook for managing top-rated restaurants
 */
export const useTopRatedRestaurants = (options = {}) => {
  const [topRated, setTopRated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTopRated = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await getTopRatedRestaurants({
        limit: options.limit || 10,
        minRatings: options.minRatings || 5
      });
      
      setTopRated(result.restaurants || []);
    } catch (err) {
      console.error('Error fetching top-rated restaurants:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [options.limit, options.minRatings]);

  useEffect(() => {
    fetchTopRated();
  }, [fetchTopRated]);

  return {
    topRated,
    loading,
    error,
    refresh: fetchTopRated
  };
};

/**
 * Hook for managing user's rating history
 */
export const useUserRatingHistory = () => {
  const [userRatings, setUserRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalRatings: 0,
    averageRating: 0,
    mostRecentRating: null
  });

  const fetchUserRatings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Note: This would require a backend endpoint to get user's rating history
      // For now, we'll implement this when the backend endpoint is available
      
      // Placeholder implementation
      setUserRatings([]);
      setStats({
        totalRatings: 0,
        averageRating: 0,
        mostRecentRating: null
      });
      
    } catch (err) {
      console.error('Error fetching user ratings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserRatings();
  }, [fetchUserRatings]);

  return {
    userRatings,
    stats,
    loading,
    error,
    refresh: fetchUserRatings
  };
};

export default useRatings;